import { z } from 'zod'

// ---------------------------------------------------------------------------
// `POST /api/v1/query/execute` — the wire format with Cadence.
//
// THIS FILE MIRRORS `types/api/contract.ts` §9 IN THE PLATFORM REPO. It is a
// transcription of an external contract, not a design of our own, and the
// snake_case naming and array-valued `verdict`/`action_type` are the
// contract's, deliberately preserved rather than translated into this
// codebase's camelCase house style. Translating here would put the drift
// somewhere invisible; keeping the wire shape verbatim means a mismatch with
// Cadence fails loudly at parse time, which is the whole point of validating.
//
// NOT to be confused with `runFilterSchema` in `cadence.ts`. That one is the
// console's *internal* URL state for the Runs page — camelCase, single-valued
// verdict, and it carries `amountAbove`, which this contract has no equivalent
// for. The two are genuinely different things that happen to describe filters;
// `ask/filter-mapping.ts` converts between them at the one boundary that needs
// it. See `docs/cadence-contract.md`.
// ---------------------------------------------------------------------------

const isoSchema = z.iso.datetime({ offset: true })

/**
 * The contract's five-value verdict union. Wider than this console's own
 * `runVerdictSchema` (four values, with `mismatch` where this says
 * `doesnt_match`, and no `not_confirmed_yet` at all) — a real divergence
 * between the console's fixture-era vocabulary and the platform's, recorded
 * in `docs/cadence-contract.md` rather than silently bridged.
 */
export const queryRunVerdictSchema = z.enum([
  'confirmed',
  'doesnt_match',
  'not_confirmed_yet',
  'needs_you',
  'refused',
])
export type QueryRunVerdict = z.infer<typeof queryRunVerdictSchema>

/** Contract §9 `RunFilter`. `.strict()` matches the contract: an unknown key is a bug, not something to ignore. */
export const queryRunFilterSchema = z
  .object({
    verdict: z.array(queryRunVerdictSchema).optional(),
    date_from: isoSchema.optional(),
    date_to: isoSchema.optional(),
    customer: z.string().optional(),
    action_type: z.array(z.string()).optional(),
    rule_fired: z.string().optional(),
    line_item_text: z.string().optional(),
  })
  .strict()
export type QueryRunFilter = z.infer<typeof queryRunFilterSchema>

export const aggregateFunctionSchema = z.enum(['count', 'sum', 'avg', 'median', 'min', 'max'])
export type AggregateFunction = z.infer<typeof aggregateFunctionSchema>

export const filterQuerySpecSchema = z
  .object({
    kind: z.literal('filter'),
    filter: queryRunFilterSchema,
    limit: z.number().int().positive().max(1000).optional(),
    offset: z.number().int().nonnegative().optional(),
  })
  .strict()

export const aggregateQuerySpecSchema = z
  .object({
    kind: z.literal('aggregate'),
    filter: queryRunFilterSchema,
    function: aggregateFunctionSchema,
    field: z.string().optional(),
    groupBy: z.string().optional(),
  })
  .strict()
  // The contract's own refinement: `field` is required for everything but
  // `count`. Expressed as a refine rather than a conditional type because zod
  // cannot express it cleanly against a non-discriminating function enum.
  .refine((q) => q.function === 'count' || q.field !== undefined, {
    message: 'field is required for every aggregate function except count',
    path: ['field'],
  })

export const breakdownQuerySpecSchema = z
  .object({
    kind: z.literal('breakdown'),
    filter: queryRunFilterSchema,
    groupBy: z.string(),
    orderBy: z.enum(['count', 'sum']),
    topN: z.number().int().positive().optional(),
  })
  .strict()

export const timelineQuerySpecSchema = z
  .object({
    kind: z.literal('timeline'),
    filter: queryRunFilterSchema,
    granularity: z.enum(['day', 'week', 'month']),
  })
  .strict()

/**
 * `compare` holds a flat array of the four leaf shapes. The contract types it
 * recursively and refuses compare-inside-compare at runtime with a 400; this
 * console never *builds* a nested compare (the Ask surface's own schema
 * forbids it), so the non-recursive form is both sufficient and stricter.
 */
export const compareQuerySpecSchema = z
  .object({
    kind: z.literal('compare'),
    queries: z.array(
      z.union([
        filterQuerySpecSchema,
        aggregateQuerySpecSchema,
        breakdownQuerySpecSchema,
        timelineQuerySpecSchema,
      ]),
    ),
  })
  .strict()

export const querySpecSchema = z.union([
  filterQuerySpecSchema,
  aggregateQuerySpecSchema,
  breakdownQuerySpecSchema,
  timelineQuerySpecSchema,
  compareQuerySpecSchema,
])
export type QuerySpec = z.infer<typeof querySpecSchema>

// ---------------------------------------------------------------------------
// Results. The null-vs-empty distinctions below are the contract's and carry
// meaning — see each field's note. Rendering must preserve them.
// ---------------------------------------------------------------------------

export const aggregateBucketSchema = z
  .object({
    /** Set only when the query declared `groupBy`. */
    group: z.string().nullable(),
    /** Set only when the aggregated field is currency-typed — never for a plain number like a quantity. */
    currency: z.string().nullable(),
    value: z.number(),
    run_count: z.number(),
  })
  .strict()
export type AggregateBucket = z.infer<typeof aggregateBucketSchema>

export const aggregateResultSchema = z
  .object({
    /** NULL when zero runs matched — "the average is undefined", never "the average is zero". */
    buckets: z.array(aggregateBucketSchema).nullable(),
  })
  .strict()

export const breakdownRowSchema = z
  .object({
    group: z.string(),
    count: z.number(),
    /** Null when not every run in the group could be priced — never zero standing in for unknown. */
    total_amount: z.number().nullable(),
    currency: z.string().nullable(),
  })
  .strict()
export type BreakdownRow = z.infer<typeof breakdownRowSchema>

export const breakdownResultSchema = z
  .object({
    /** Empty array, not null: "zero groups" is a real answer here, unlike an aggregate's undefined average. */
    rows: z.array(breakdownRowSchema),
  })
  .strict()

export const timelineBucketSchema = z
  .object({
    period_start: isoSchema,
    count: z.number(),
    total_amount: z.number().nullable(),
    currency: z.string().nullable(),
  })
  .strict()
export type TimelineBucket = z.infer<typeof timelineBucketSchema>

export const timelineResultSchema = z
  .object({
    /** One entry per period across the FULL range, zero-count periods included — a sparse array cannot tell "no activity" from "no data". */
    buckets: z.array(timelineBucketSchema),
  })
  .strict()

/**
 * The `filter` result's rows. Typed loosely on purpose: these are Cadence's
 * `Run` objects in the *platform's* shape, which differs field-for-field from
 * this console's `runSummarySchema`. The Ask surface never renders them
 * directly — a `filter` result routes to the Runs page, which fetches through
 * the existing client — so parsing them into a second, competing run type here
 * would create exactly the drift this file exists to avoid.
 */
export const filterResultSchema = z
  .object({
    data: z.array(z.unknown()),
    total: z.number(),
    offset: z.number(),
    limit: z.number(),
  })
  .strict()

const leafQueryResultSchema = z.union([
  filterResultSchema,
  aggregateResultSchema,
  breakdownResultSchema,
  timelineResultSchema,
])

export const compareResultSchema = z
  .object({ results: z.array(leafQueryResultSchema.nullable()) })
  .strict()

export const queryResultSchema = z.union([leafQueryResultSchema, compareResultSchema])
export type QueryResult = z.infer<typeof queryResultSchema>

export const queryExecuteRequestSchema = z.object({ query: querySpecSchema }).strict()
export type QueryExecuteRequest = z.infer<typeof queryExecuteRequestSchema>

export const queryExecuteResponseSchema = z
  .object({
    data: z
      .object({
        kind: z.enum(['filter', 'aggregate', 'breakdown', 'timeline', 'compare']),
        result: queryResultSchema,
        /** Exactly what ran — a normalised spec may differ from what was asked (a default limit filled in, say). */
        query_echoed: querySpecSchema,
      })
      .strict(),
    /**
     * CAD-008. Same envelope field as every other response — added here too
     * so `/query/execute` and `/reference/vocabulary` are not the two routes
     * whose provenance the console cannot check.
     */
    tenant_id: z.string(),
    tenant_source: z.enum(['header', 'deployment-binding']),
    as_of: isoSchema,
  })
  .strict()
export type QueryExecuteResponse = z.infer<typeof queryExecuteResponseSchema>
